import { node, createPremiumIcon } from "~/util/dom";
import Dialog from "~/ui/dialog";
import { _ } from "~/i18n";
import { CONFIG } from "~/config";
import { store } from "~/state/store";
import { getProductInfo, IProductInfo } from "~/util/providers/premium";
import PaddleWrapper from "~/util/paddleWrapper";
import { getUserEmail } from "~/util/user";
import Tooltip, { ETooltipFlow } from "~/ui/tooltip";
import { ELoginState } from "~/state/user/interfaces";
import CouponActivationDialog from "~/ui/dialogs/coupon-activation-dialog";
import { createPrivileges } from "~/ui/premium/privileges";

let dialog = null;

function openNoEmailDialog() {
	const container = node("div", { className: "premiumPropositionNoEmail__container" });
	const noEmailDialog = new Dialog({
		className: "premiumPropositionNoEmail",
		onClose: () => {
			noEmailDialog.destroy();
		},
	});
	const title = node("h3", { className: "premiumPropositionNoEmail__title" }, _("premium.proposition.popupNoEmail.title"));
	const description = node("p", { className: "premiumPropositionNoEmail__description" }, _("premium.proposition.popupNoEmail.description"));

	container.append(title, description);

	noEmailDialog.addBtn({
		title: _("premium.proposition.popupNoEmail.buttonAdd"),
		bindEnter: true,
		cb: () => {
			noEmailDialog.close();

			window.open("https://login.szn.cz/assign-email");
		},
		type: "ok",
	});

	noEmailDialog.addBtn({
		title: _("premium.proposition.popupNoEmail.buttonCancel"),
		cb: () => {
			noEmailDialog.close();
		},
	});

	noEmailDialog.dom.content.append(container);

	noEmailDialog.open();
}

function buildBuyButton(button: HTMLButtonElement, productInfo: IProductInfo, purchaseOrigin = "") {
	const currentProductInfo = productInfo.data[0];
	const currentProductPrice = currentProductInfo.prices[currentProductInfo.platformProduct[0]];
	const col = node("div", { className: "premiumProposition__button_col" });
	const price = node("div", { className: "premiumProposition__button_price" }, _("premium.proposition.popup.byYear"));
	const priceStrong = node("strong", { className: "premiumProposition__button_price_by" }, new Intl.NumberFormat(CONFIG.defaultLang, {
		style: "currency",
		currency: currentProductPrice.currency,
		trailingZeroDisplay: "stripIfInteger",
	}).format(
		currentProductPrice.amount / 100,
	));
	const textPremium = node("p", { className: "premiumProposition__button_textPremium" }, _("premium.proposition.popup.premium"));
	const textExtension = node("p", { className: "premiumProposition__button_text" }, _("premium.proposition.popup.payWithExtension"));
	const textTrial = node("div", { className: "premiumProposition__button_textTrial" }, _("premium.proposition.popup.trialText", {
		number: 7,
	}));
	const user = store.getState().user.user;
	const premiumData = user.premiumData;

	button.innerHTML = "";

	price.prepend(priceStrong);

	col.append(textPremium, textExtension);

	button.append(col, price);

	button.addEventListener("click", event => {
		const actualUser = store.getState().user.user;
		const openPaddle = async () => {
			await PaddleWrapper.openPaddle(currentProductInfo.platformProduct[0], actualUser, purchaseOrigin);

			button.innerHTML = "";
			button.append(node("mapy-loader"));

			Mapy.getComponent("logger").log("premiumPurchase_popup", {
				purchaseOrigin,
			});

			dialog.close();
		};

		if (actualUser.state === ELoginState.Green) {
			if (getUserEmail()) {
				openPaddle();
			} else {
				event.stopPropagation();
				dialog.close();

				openNoEmailDialog();
			}
		} else {
			Mapy.getComponent("login").open({
				analyticsData: { loginOrigin: purchaseOrigin },
				motivation: "none",
			}).then(() => {
				const premium = store.getState().user.user.premium;

				dialog.close();

				if (!premium) {
					// eslint-disable-next-line @typescript-eslint/no-use-before-define
					openPropositionDialog(purchaseOrigin);
				}
			}, () => {});
		}
	});

	if (premiumData?.profile?.isTrialAvailable) {
		button.append(textTrial);
	}
}

export function openPropositionDialog(purchaseOrigin = "") {
	const openCouponDialog = () => {
		let couponDialog: CouponActivationDialog = null;
		const dispose = () => {
			couponDialog?.destroy();
			couponDialog = null;
		};

		dialog?.close();

		couponDialog = new CouponActivationDialog({
			onClose: dispose,
			onBack: () => {
				dispose();
				openPropositionDialog(purchaseOrigin);
			},
			onFinished: async data => {
				dispose();
				await Mapy.getComponent("login").relogin();

				Mapy.getComponent("logger").log("premiumPurchase_success", {
					purchaseOrigin: data?.subscriptions?.length ? data.subscriptions[0].subscriptionOrigin : "coupon",
				});

				Mapy.getComponent("premiumStatus").activate();
			},
		});
	};

	const container = node("div", { className: "premiumProposition__container" });

	dialog = new Dialog({
		className: "premiumProposition",
		onClose: () => {
			dialog.destroy();
			dialog = null;
		},
	});

	// titulek
	const title = node("h3", { className: "premiumProposition__title" }, _("premium.proposition.popup.title"));
	const mapyPremium = node("strong", {}, _("premium.proposition.popup.premium"));

	mapyPremium.prepend(createPremiumIcon());

	title.append(mapyPremium);

	// list vyhod premia
	const list = createPrivileges({
		place: "buy-dialog",
	});
	// tlacitko na zaplaceni
	const button = node("button", { type: "button", className: "premiumProposition__button" });

	// zatim loader nez nacteme cenu
	button.append(node("mapy-loader"));

	getProductInfo().then(data => {
		if (data) {
			buildBuyButton(button, data, purchaseOrigin);
		}
	}, () => {});

	const bottom = node("div", { className: "premiumProposition__textBottom" });
	const bottomConditions = node("p");

	bottomConditions.innerHTML = _("premium.proposition.popup.bottom", {
		conditions: node("a", {
			href: `https://licence.mapy.cz/?doc=mapy_pu&lang=${CONFIG.defaultLang}`,
			target: "_blank",
		}, _("premium.proposition.popup.bottom.conditions")).outerHTML,
		protectionPolicy: node("a", {
			href: `https://licence.mapy.cz/?doc=mapy_zzoo&lang=${CONFIG.defaultLang}`,
			target: "_blank",
		}, _("premium.proposition.popup.bottom.protectionPolicy")).outerHTML,
	});

	const bottominfo = node("p", {}, _("premium.proposition.popup.bottom.info"));
	const coupon = node("p", { className: "premiumProposition__coupon" });

	coupon.innerHTML = _("premium.proposition.popup.bottom.coupon", {
		button: node("button", { type: "button" }, _("premium.proposition.popup.bottom.couponHier")).outerHTML,
	});

	coupon.querySelector("button").addEventListener("click", () => {
		const { user } = store.getState().user;

		if (user.state === ELoginState.Green) {
			openCouponDialog();
		} else {
			Mapy.getComponent("login").open({
				analyticsData: { loginOrigin: purchaseOrigin },
				motivation: "none",
			}).then(() => {
				openCouponDialog();
			}, () => {});
		}
	});

	bottom.append(bottomConditions, bottominfo, coupon);

	container.append(title, list, button, bottom);

	// info pro cestinu, ze paddle neumi cestinu ale jen en
	if (CONFIG.defaultLang === "cs") {
		const infoText = node("p", { className: "premiumProposition__info" });

		infoText.innerHTML = _("premium.proposition.popup.info", {
			TERMS: node("a", {
				href: "https://www.paddle.com/legal/checkout-buyer-terms",
				title: _("premium.proposition.popup.info.terms"),
				target: "_blank",
			}, _("premium.proposition.popup.info.terms")).outerHTML,
			PRIVACY: node("a", {
				href: "https://www.paddle.com/legal/privacy",
				title: _("premium.proposition.popup.info.privacy"),
				target: "_blank",
			}, _("premium.proposition.popup.info.privacy")).outerHTML,
		});


		button.after(infoText);
	}

	dialog.dom.container.append(container);

	dialog.open();

	PaddleWrapper.initialize(event => {
		if (event.name === "checkout.loaded") {
			dialog?.close();
		}
	});
}

export function createPremiumIconWithTooltip() {
	const premiumIcon = createPremiumIcon();
	const premium = `<a href="">${_("premium.proposition.popup.premium")}</a>`;
	const content = node("p", { innerHTML: _("route.params.premiumFunction", { premium }) });
	const link = content.querySelector("a") as HTMLAnchorElement;

	premiumIcon.style.cursor = "pointer";
	premiumIcon.addEventListener("click", () => {
		openPropositionDialog();
	});
	link.addEventListener("click", event => {
		event.stopPropagation();
		event.preventDefault();
		openPropositionDialog();
	});

	/* eslint-disable-next-line */
	const tooltip = new Tooltip({
		anchor: premiumIcon,
		trigger: premiumIcon,
		triggerEventType: "pointerenter",
		content,
		arrow: true,
		flow: ETooltipFlow.ARROW_END,
		offset: [0, 0, 2, 0],
		className: "route-params-premium-tooltip",
		hideTimeout: 500,
		width: 208,
	});

	return premiumIcon;
}
